import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Service } from '../../../services/servicesStore';
import type { Client, ClientStatus } from '../../../services/clientService';
import { currencyService } from '../../../services/currencyService';
import type {
  AdminSubscription,
  SubscriptionBillingStatus,
  SubscriptionInterval,
  SubscriptionServiceStatus,
  SubscriptionStatus,
} from '../../../services/adminSubscriptionService';
import type {
  AdminOrder,
  OrderFulfillmentStatus,
  OrderPaymentStatus,
} from '../../../services/adminOrderService';

interface ClientDetailsModalProps {
  client: Client | null;
  services: Service[];
  relatedSubscriptions: AdminSubscription[];
  relatedOrders: AdminOrder[];
  defaultMode?: 'view' | 'create';
  onClose: () => void;
  onSave: (client: Client) => Promise<void> | void;
}

const STATUS_LABELS: Record<ClientStatus, string> = {
  pending: 'En attente',
  active: 'En cours',
  completed: 'Termine',
  canceled: 'Annule',
};

const SUBSCRIPTION_STATUS_LABELS: Record<SubscriptionStatus, string> = {
  trial: 'Essai',
  active: 'Actif',
  paused: 'En pause',
  past_due: 'En retard',
  canceled: 'Annule',
  expired: 'Expire',
};

const SUBSCRIPTION_STATUS_BADGES: Record<SubscriptionStatus, string> = {
  trial: 'bg-slate-100 text-slate-700',
  active: 'bg-emerald-100 text-emerald-700',
  paused: 'bg-amber-100 text-amber-700',
  past_due: 'bg-red-100 text-red-700',
  canceled: 'bg-gray-100 text-gray-600',
  expired: 'bg-orange-100 text-orange-700',
};

const BILLING_STATUS_LABELS: Record<SubscriptionBillingStatus, string> = {
  scheduled: 'Planifie',
  paid: 'Paye',
  overdue: 'En retard',
  unpaid: 'Impayee',
};

const BILLING_STATUS_BADGES: Record<SubscriptionBillingStatus, string> = {
  scheduled: 'bg-indigo-100 text-indigo-700',
  paid: 'bg-emerald-100 text-emerald-700',
  overdue: 'bg-red-100 text-red-700',
  unpaid: 'bg-amber-100 text-amber-700',
};

const SERVICE_STATUS_LABELS: Record<SubscriptionServiceStatus, string> = {
  pending_setup: 'Setup en attente',
  onboarding: 'Onboarding',
  active_delivery: 'Livraison active',
  delivered: 'Livre / actif',
  paused: 'En pause',
  closed: 'Cloture',
};

const SERVICE_STATUS_BADGES: Record<SubscriptionServiceStatus, string> = {
  pending_setup: 'bg-slate-100 text-slate-700',
  onboarding: 'bg-blue-100 text-blue-700',
  active_delivery: 'bg-indigo-100 text-indigo-700',
  delivered: 'bg-emerald-100 text-emerald-700',
  paused: 'bg-amber-100 text-amber-700',
  closed: 'bg-gray-100 text-gray-600',
};

const INTERVAL_LABELS: Record<SubscriptionInterval, string> = {
  monthly: 'Mensuel',
  quarterly: 'Trimestriel',
  yearly: 'Annuel',
};

const ORDER_PAYMENT_LABELS: Record<OrderPaymentStatus, string> = {
  draft: 'Brouillon',
  awaiting_payment: 'En attente',
  partially_paid: 'Partiellement paye',
  paid: 'Paye',
  refunded: 'Rembourse',
  canceled: 'Annule',
};

const ORDER_PAYMENT_BADGES: Record<OrderPaymentStatus, string> = {
  draft: 'bg-slate-100 text-slate-700',
  awaiting_payment: 'bg-amber-100 text-amber-700',
  partially_paid: 'bg-blue-100 text-blue-700',
  paid: 'bg-emerald-100 text-emerald-700',
  refunded: 'bg-gray-100 text-gray-600',
  canceled: 'bg-red-100 text-red-700',
};

const ORDER_FULFILLMENT_LABELS: Record<OrderFulfillmentStatus, string> = {
  new: 'Nouveau',
  scheduled: 'Planifie',
  in_progress: 'En cours',
  delivered: 'Livre',
  completed: 'Termine',
  on_hold: 'En pause',
  canceled: 'Annule',
};

const ORDER_FULFILLMENT_BADGES: Record<OrderFulfillmentStatus, string> = {
  new: 'bg-slate-100 text-slate-700',
  scheduled: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-indigo-100 text-indigo-700',
  delivered: 'bg-emerald-100 text-emerald-700',
  completed: 'bg-emerald-100 text-emerald-700',
  on_hold: 'bg-amber-100 text-amber-700',
  canceled: 'bg-red-100 text-red-700',
};

const SUBSCRIPTION_PRIORITY: Record<SubscriptionStatus, number> = {
  active: 0,
  trial: 1,
  past_due: 2,
  paused: 3,
  canceled: 4,
  expired: 5,
};

const formatDateTime = (value?: string) => {
  if (!value) {
    return 'Non renseigne';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Non renseigne';
  }

  return parsed.toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ClientDetailsModal = ({
  client,
  services,
  relatedSubscriptions,
  relatedOrders,
  defaultMode = 'view',
  onClose,
  onSave,
}: ClientDetailsModalProps) => {
  const [editMode, setEditMode] = useState(defaultMode === 'create');
  const [formData, setFormData] = useState<Client | null>(client);
  const [displayCountry, setDisplayCountry] = useState(currencyService.getCountry());
  const [isSaving, setIsSaving] = useState(false);
  const submitRequestedRef = useRef(false);

  useEffect(() => {
    setFormData(client);
    setEditMode(defaultMode === 'create');
    submitRequestedRef.current = false;
  }, [client, defaultMode]);

  useEffect(() => {
    return currencyService.subscribe((nextCountry) => {
      setDisplayCountry(nextCountry);
    });
  }, []);

  useEffect(() => {
    if (!client) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [client, onClose]);

  if (!client || !formData) return null;

  const setField = <K extends keyof Client>(field: K, value: Client[K]) => {
    setFormData((current) => (current ? { ...current, [field]: value } : current));
  };

  const formattedPurchaseDate = formData.purchaseDate
    ? new Date(formData.purchaseDate).toISOString().slice(0, 10)
    : '';
  const formattedLastContactAt = formData.lastContactAt
    ? new Date(formData.lastContactAt).toISOString().slice(0, 10)
    : '';
  const displayCurrencyCode = currencyService.getDisplayCurrencyCode(displayCountry);
  const originalAmountLabel = currencyService.formatCurrencyAmount(
    formData.totalAmount,
    formData.currencyCode,
    { display: 'code' }
  );
  const displayAmountLabel = currencyService.formatAmountForDisplay(
    formData.totalAmount,
    formData.currencyCode,
    {
      country: displayCountry,
      display: 'code',
    }
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!submitRequestedRef.current) {
      return;
    }
    submitRequestedRef.current = false;

    if (!formData) {
      return;
    }

    setIsSaving(true);
    try {
      await Promise.resolve(onSave(formData));
      if (defaultMode !== 'create') {
        setEditMode(false);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const armSubmitIntent = () => {
    submitRequestedRef.current = true;
    window.setTimeout(() => {
      submitRequestedRef.current = false;
    }, 0);
  };

  const sortedSubscriptions = [...relatedSubscriptions].sort((left, right) => {
    const statusDelta =
      (SUBSCRIPTION_PRIORITY[left.status] ?? 99) -
      (SUBSCRIPTION_PRIORITY[right.status] ?? 99);

    if (statusDelta !== 0) {
      return statusDelta;
    }

    const leftTime = left.nextBillingDate
      ? new Date(left.nextBillingDate).getTime()
      : Number.MAX_SAFE_INTEGER;
    const rightTime = right.nextBillingDate
      ? new Date(right.nextBillingDate).getTime()
      : Number.MAX_SAFE_INTEGER;

    return leftTime - rightTime;
  });
  const sortedOrders = [...relatedOrders].sort(
    (left, right) =>
      new Date(right.orderedAt || right.lastUpdateAt).getTime() -
      new Date(left.orderedAt || left.lastUpdateAt).getTime()
  );
  const primarySubscription = sortedSubscriptions[0];
  const latestOrder = sortedOrders[0];
  const billingType = primarySubscription ? 'Abonnement' : 'Paiement unique';
  const nextPaymentLabel = primarySubscription?.nextBillingDate
    ? formatDateTime(primarySubscription.nextBillingDate)
    : 'Aucun paiement planifie';
  const lastPaymentLabel = primarySubscription?.lastBillingDate
    ? formatDateTime(primarySubscription.lastBillingDate)
    : latestOrder?.paidAt
      ? formatDateTime(latestOrder.paidAt)
      : client.purchaseDate
        ? formatDateTime(client.purchaseDate)
        : 'Non renseigne';

  return (
    <AnimatePresence>
      <div
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
          onClick={(event) => event.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-6 py-4">
            <h2 className="text-xl font-semibold">
              {editMode ? 'Modifier le client' : 'Détails du client'}
            </h2>
            <button
              type="button"
              onClick={() => {
                submitRequestedRef.current = false;
                onClose();
              }}
              aria-label="Fermer la fenêtre client"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 text-2xl leading-none text-gray-700 transition-colors hover:bg-gray-200"
            >
              ×
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto p-6">
            {editMode ? (
              <form id="client-details-form" onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setField('name', e.target.value)}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setField('email', e.target.value)}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                      required
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      value={formData.phone || ''}
                      onChange={(e) => setField('phone', e.target.value)}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Entreprise
                    </label>
                    <input
                      type="text"
                      value={formData.company || ''}
                      onChange={(e) => setField('company', e.target.value)}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Numero d&apos;entreprise
                    </label>
                    <input
                      type="text"
                      value={formData.companyNumber || ''}
                      onChange={(e) => setField('companyNumber', e.target.value)}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                      placeholder="Ex: CHE-499.459.668"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Adresse
                    </label>
                    <input
                      type="text"
                      value={formData.address || ''}
                      onChange={(e) => setField('address', e.target.value)}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                      placeholder="Rue, numero, ville, pays"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Service
                    </label>
                    <select
                      value={formData.serviceId}
                      onChange={(e) => {
                        const service = services.find(
                          (entry) => entry.id === e.target.value
                        );
                        setFormData({
                          ...formData,
                          serviceId: e.target.value,
                          serviceName: service?.name || '',
                        });
                      }}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    >
                      <option value="">Aucun service</option>
                      {services.map((service) => (
                        <option key={service.id} value={service.id}>
                          {service.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Statut
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) =>
                        setField('status', e.target.value as Client['status'])
                      }
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    >
                      <option value="pending">En attente</option>
                      <option value="active">En cours</option>
                      <option value="completed">Termine</option>
                      <option value="canceled">Annule</option>
                    </select>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date d&apos;achat
                    </label>
                    <input
                      type="date"
                      value={formattedPurchaseDate}
                      onChange={(e) =>
                        setField(
                          'purchaseDate',
                          e.target.value
                            ? new Date(`${e.target.value}T12:00:00`).toISOString()
                            : new Date().toISOString()
                        )
                      }
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Montant
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.totalAmount}
                      onChange={(e) =>
                        setField('totalAmount', Number(e.target.value || 0))
                      }
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    />
                    <div className="mt-2 text-xs text-gray-500">
                      Affichage admin actuel: {displayAmountLabel}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Devise
                    </label>
                    <select
                      value={formData.currencyCode}
                      onChange={(e) => setField('currencyCode', e.target.value)}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    >
                      <option value="EUR">EUR</option>
                      <option value="CAD">CAD</option>
                      <option value="CHF">CHF</option>
                      <option value="USD">USD</option>
                      <option value="GBP">GBP</option>
                    </select>
                    <div className="mt-2 text-xs text-gray-500">
                      Devise affichée dans l&apos;admin: {displayCurrencyCode}
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Dernier contact
                    </label>
                    <input
                      type="date"
                      value={formattedLastContactAt}
                      onChange={(e) =>
                        setField(
                          'lastContactAt',
                          e.target.value
                            ? new Date(`${e.target.value}T12:00:00`).toISOString()
                            : ''
                        )
                      }
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Source
                    </label>
                    <select
                      value={formData.source}
                      onChange={(e) =>
                        setField('source', e.target.value as Client['source'])
                      }
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    >
                      <option value="manual">Manuel</option>
                      <option value="payment">Paiement</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes || ''}
                    onChange={(e) => setField('notes', e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    rows={4}
                  />
                </div>
              </form>
            ) : (
              <div className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                    <div className="text-sm font-medium text-gray-500">Statut client</div>
                    <div className="mt-3">
                      <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium bg-gray-100 text-gray-800">
                        {STATUS_LABELS[client.status]}
                      </span>
                    </div>
                  </div>
                  <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                    <div className="text-sm font-medium text-gray-500">Mode de facturation</div>
                    <div className="mt-3 text-lg font-semibold text-gray-900">
                      {billingType}
                    </div>
                    {primarySubscription && (
                      <div className="mt-1 text-xs text-gray-500">
                        {INTERVAL_LABELS[primarySubscription.interval]}
                      </div>
                    )}
                  </div>
                  <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                    <div className="text-sm font-medium text-gray-500">Prochain paiement</div>
                    <div className="mt-3 text-lg font-semibold text-gray-900">
                      {nextPaymentLabel}
                    </div>
                    {primarySubscription && (
                      <div className="mt-2">
                        <span
                          className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${BILLING_STATUS_BADGES[primarySubscription.billingStatus]}`}
                        >
                          {BILLING_STATUS_LABELS[primarySubscription.billingStatus]}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                    <div className="text-sm font-medium text-gray-500">Montant suivi</div>
                    <div className="mt-3 text-2xl font-bold text-gray-900">
                      {displayAmountLabel}
                    </div>
                    {client.currencyCode !== displayCurrencyCode && (
                      <div className="text-xs text-gray-400 mt-1">
                        Origine: {originalAmountLabel}
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm font-medium text-gray-500">Nom</div>
                    <div className="mt-1">{client.name}</div>
                  </div>

                  <div>
                    <div className="text-sm font-medium text-gray-500">Email</div>
                    <div className="mt-1">{client.email}</div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  {client.phone && (
                    <div>
                      <div className="text-sm font-medium text-gray-500">Téléphone</div>
                      <div className="mt-1">{client.phone}</div>
                    </div>
                  )}

                  {client.company && (
                    <div>
                      <div className="text-sm font-medium text-gray-500">Entreprise</div>
                      <div className="mt-1">{client.company}</div>
                    </div>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  {client.companyNumber && (
                    <div>
                      <div className="text-sm font-medium text-gray-500">
                        Numero d&apos;entreprise
                      </div>
                      <div className="mt-1">{client.companyNumber}</div>
                    </div>
                  )}

                  {client.address && (
                    <div>
                      <div className="text-sm font-medium text-gray-500">Adresse</div>
                      <div className="mt-1 whitespace-pre-wrap">{client.address}</div>
                    </div>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm font-medium text-gray-500">Service</div>
                    <div className="mt-1">{client.serviceName || 'Non attribue'}</div>
                  </div>

                  <div>
                    <div className="text-sm font-medium text-gray-500">Date d&apos;achat</div>
                    <div className="mt-1">
                      {new Date(client.purchaseDate).toLocaleDateString('fr-FR')}
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <div className="text-sm font-medium text-gray-500">Statut</div>
                    <div className="mt-1">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {STATUS_LABELS[client.status]}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-500">Montant</div>
                    <div className="mt-1">
                      {displayAmountLabel}
                      {client.currencyCode !== displayCurrencyCode && (
                        <div className="text-xs text-gray-400 mt-1">
                          Origine: {originalAmountLabel}
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-500">Source</div>
                    <div className="mt-1">
                      {client.source === 'payment' ? 'Paiement' : 'Manuel'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-500">Dernier paiement</div>
                    <div className="mt-1">{lastPaymentLabel}</div>
                  </div>
                </div>

                {client.lastContactAt && (
                  <div>
                    <div className="text-sm font-medium text-gray-500">Dernier contact</div>
                    <div className="mt-1">
                      {new Date(client.lastContactAt).toLocaleDateString('fr-FR')}
                    </div>
                  </div>
                )}

                <div>
                  <div className="text-sm font-medium text-gray-500">Notes</div>
                  <div className="mt-1 whitespace-pre-wrap text-gray-700">
                    {client.notes || 'Aucune note pour le moment.'}
                  </div>
                </div>

                <div className="grid gap-6 lg:grid-cols-2">
                  <section className="rounded-2xl border border-gray-200 p-5">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Gestion abonnement
                    </h3>

                    {primarySubscription ? (
                      <div className="mt-4 space-y-4">
                        <div className="flex flex-wrap gap-2">
                          <span
                            className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${SUBSCRIPTION_STATUS_BADGES[primarySubscription.status]}`}
                          >
                            {SUBSCRIPTION_STATUS_LABELS[primarySubscription.status]}
                          </span>
                          <span
                            className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${BILLING_STATUS_BADGES[primarySubscription.billingStatus]}`}
                          >
                            {BILLING_STATUS_LABELS[primarySubscription.billingStatus]}
                          </span>
                          <span
                            className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${SERVICE_STATUS_BADGES[primarySubscription.serviceStatus]}`}
                          >
                            {SERVICE_STATUS_LABELS[primarySubscription.serviceStatus]}
                          </span>
                        </div>

                        <div className="space-y-2 text-sm text-gray-700">
                          <div>
                            <strong>Abonnement :</strong> {primarySubscription.subscriptionNumber}
                          </div>
                          <div>
                            <strong>Frequence :</strong> {INTERVAL_LABELS[primarySubscription.interval]}
                          </div>
                          <div>
                            <strong>Montant :</strong> {currencyService.formatAmountForDisplay(primarySubscription.amount, primarySubscription.currencyCode, {
                              country: displayCountry,
                              display: 'code',
                            })}
                          </div>
                          <div>
                            <strong>Prochaine echeance :</strong> {formatDateTime(primarySubscription.nextBillingDate)}
                          </div>
                          <div>
                            <strong>Dernier paiement :</strong> {formatDateTime(primarySubscription.lastBillingDate)}
                          </div>
                          <div>
                            <strong>Fin / resiliation :</strong> {formatDateTime(primarySubscription.endDate)}
                          </div>
                        </div>

                        {sortedSubscriptions.length > 1 && (
                          <div className="border-t pt-4">
                            <div className="text-sm font-medium text-gray-900 mb-2">
                              Autres abonnements lies
                            </div>
                            <div className="space-y-2">
                              {sortedSubscriptions.slice(1, 4).map((subscription) => (
                                <div
                                  key={subscription.id}
                                  className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700"
                                >
                                  <div className="font-medium text-gray-900">
                                    {subscription.subscriptionNumber}
                                  </div>
                                  <div className="mt-1">
                                    {INTERVAL_LABELS[subscription.interval]} • {SUBSCRIPTION_STATUS_LABELS[subscription.status]}
                                  </div>
                                  <div className="mt-1 text-xs text-gray-500">
                                    Prochaine echeance: {formatDateTime(subscription.nextBillingDate)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="mt-4 rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-600">
                        Aucun abonnement lie pour ce client. Ce dossier est actuellement gere en paiement unique.
                      </div>
                    )}
                  </section>

                  <section className="rounded-2xl border border-gray-200 p-5">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Commandes liees
                    </h3>

                    {sortedOrders.length > 0 ? (
                      <div className="mt-4 space-y-3">
                        {sortedOrders.slice(0, 4).map((order) => (
                          <div
                            key={order.id}
                            className="rounded-xl border border-gray-200 bg-gray-50 p-4"
                          >
                            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                              <div>
                                <div className="font-medium text-gray-900">
                                  {order.orderNumber}
                                </div>
                                <div className="mt-1 text-sm text-gray-600">
                                  {order.serviceName || 'Service non renseigne'}
                                </div>
                                <div className="mt-1 text-xs text-gray-500">
                                  {formatDateTime(order.orderedAt)}
                                </div>
                              </div>

                              <div className="space-y-2 text-right">
                                <div className="text-sm font-semibold text-gray-900">
                                  {currencyService.formatAmountForDisplay(order.amount, order.currencyCode, {
                                    country: displayCountry,
                                    display: 'code',
                                  })}
                                </div>
                                <div className="flex flex-wrap justify-end gap-2">
                                  <span
                                    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${ORDER_PAYMENT_BADGES[order.paymentStatus]}`}
                                  >
                                    {ORDER_PAYMENT_LABELS[order.paymentStatus]}
                                  </span>
                                  <span
                                    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${ORDER_FULFILLMENT_BADGES[order.fulfillmentStatus]}`}
                                  >
                                    {ORDER_FULFILLMENT_LABELS[order.fulfillmentStatus]}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="mt-4 rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-600">
                        Aucune commande liee pour le moment.
                      </div>
                    )}
                  </section>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 flex justify-end gap-4">
            {editMode ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    submitRequestedRef.current = false;
                    if (defaultMode === 'create') {
                      onClose();
                      return;
                    }

                    setFormData(client);
                    setEditMode(false);
                  }}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  form="client-details-form"
                  onClick={armSubmitIntent}
                  disabled={isSaving}
                  className="px-4 py-2 bg-neon-orange text-white rounded-lg hover:bg-[#FF4500] transition-colors"
                >
                  {isSaving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => {
                    submitRequestedRef.current = false;
                    onClose();
                  }}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
                >
                  Fermer
                </button>
                <button
                  type="button"
                  onClick={() => {
                    submitRequestedRef.current = false;
                    setEditMode(true);
                  }}
                  className="px-4 py-2 bg-neon-orange text-white rounded-lg hover:bg-[#FF4500] transition-colors"
                >
                  Modifier
                </button>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ClientDetailsModal;
